import React from 'react';
import { Link } from 'react-router-dom';

export const ServicesPage = () => {
  const services = [
    {
      title: 'Container Drayage',
      description: 'Expert import and export container drayage services from NY/NJ ports.',
      details: 'Our container drayage service is the backbone of our business. We specialize in moving containers from ports to warehouses, distribution centers, or directly to your facility. With direct access to all major NY/NJ port terminals including Newark, Elizabeth, and Bayonne, we ensure quick turnaround times and competitive rates.',
      features: ['20\', 40\', and 45\' containers', 'Same-day and next-day service', 'Chassis pool management', 'Direct port access'],
      icon: '🚚',
      image: 'https://customer-assets.emergentagent.com/job_cargo-pricing/artifacts/ftcr9ywc_Pic2.jpg',
    },
    {
      title: 'Warehousing / Transloading',
      description: 'We keep your goods secure and ensure they arrive without any hassle.',
      details: 'Our warehousing and transloading services provide flexible solutions for your storage and distribution needs. Whether you need short-term storage while coordinating deliveries or long-term warehousing for inventory management, our secure facilities are equipped to handle your cargo safely.',
      features: ['Climate-controlled storage', 'Cross-docking services', 'Inventory management', 'Pick and pack services'],
      icon: '🏭',
      image: 'https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBzdG9yYWdlfGVufDB8fHx8MTc2MjM4NTQ4M3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Container Storage',
      description: 'Secure container storage facilities for your cargo needs.',
      details: 'Need extra time before your container is ready for delivery? Our container storage facilities provide secure, accessible storage for loaded and empty containers. Daily, weekly, or monthly rates available.',
      features: ['24/7 security surveillance', 'Easy in/out access', 'Flexible rental terms', 'Per diem management'],
      icon: '📦',
      image: 'https://customer-assets.emergentagent.com/job_cargo-pricing/artifacts/e6anxd6j_IMG_9400-scaled.jpg',
    },
    {
      title: 'Permits & Compliance',
      description: 'Overweight and alcohol permits handled by our expert team.',
      details: 'Navigating permits and regulations can be complex. Our experienced team specializes in handling all the paperwork for overweight permits and alcohol shipment permits, ensuring your cargo stays compliant with all state regulations and reaches its destination without delays.',
      features: ['Overweight permits', 'Alcohol shipment permits', 'Fast processing', 'State-specific compliance'],
      icon: '📋',
      image: 'https://images.pexels.com/photos/6720534/pexels-photo-6720534.jpeg',
    },
    {
      title: 'Reefer Container',
      description: 'Temperature-controlled container solutions for sensitive cargo.',
      details: 'Transporting perishables, pharmaceuticals, or other temperature-sensitive goods? Our reefer container service maintains precise temperature control throughout transit, with 24/7 monitoring to ensure your cargo arrives in perfect condition.',
      features: ['Temperature monitoring', 'Frozen and chilled options', 'Generator-powered units', 'Backup power systems'],
      icon: '❄️',
      image: 'https://images.unsplash.com/photo-1619679610144-0d5d42196d57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxyZWVmZXIlMjBjb250YWluZXJ8ZW58MHx8fHwxNzYyMzg4NDcyfDA&ixlib=rb-4.1.0&q=85',
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Our Services</h1>
          <p className="text-xl max-w-3xl mx-auto">Specialized freight services to handle your unique cargo requirements, backed by years of experience and a commitment to excellence.</p>
        </div>
      </section>

      <div className="py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Everything You Need for Your Supply Chain</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From container pickup to final delivery, we provide end-to-end logistics services that keep your business moving. Our experienced team and modern fleet ensure reliable, efficient service every time.
            </p>
          </div>

          <div className="space-y-16">
            {services.map((service, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`} data-testid="service-item">
                <div className="md:w-1/2">
                  <div className="bg-white p-8 rounded-lg shadow-xl">
                    <div className="text-6xl mb-4">{service.icon}</div>
                    <h3 className="text-3xl font-bold mb-4">{service.title}</h3>
                    <p className="text-gray-700 text-lg mb-6 leading-relaxed">{service.details}</p>
                    <h4 className="font-bold text-lg mb-3">Key Features:</h4>
                    <ul className="grid grid-cols-2 gap-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="md:w-1/2">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Additional Services */}
          <div className="mt-20 bg-gray-50 rounded-lg p-12">
            <h2 className="text-3xl font-bold mb-8 text-center">Additional Services</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🔒</span>
                </div>
                <h3 className="font-bold text-xl mb-3">Secure Cargo Handling</h3>
                <p className="text-gray-600 leading-relaxed">High-value and sensitive cargo handled with enhanced security protocols. We provide extra bold seal for your containers, and dedicated handling procedures to ensure your valuable shipments arrive safely and securely.</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">⚡</span>
                </div>
                <h3 className="font-bold text-xl mb-3">Expedited Service</h3>
                <p className="text-gray-600 leading-relaxed">Need it there fast? Our expedited service offers same-day and next-day delivery options for urgent shipments. Priority handling, direct routes, and real-time updates keep your time-critical cargo moving without delays.</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📊</span>
                </div>
                <h3 className="font-bold text-xl mb-3">Reporting</h3>
                <p className="text-gray-600 leading-relaxed">We provide you with detailed reports on delivery date & times, costs and container status at the ports.</p>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center bg-blue-600 text-white p-12 rounded-lg">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience Superior Service?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">Get a customized quote for your shipping needs. Our team is ready to provide solutions tailored to your business.</p>
            <Link to="/get-quote#quote-form-top" className="bg-white text-blue-600 px-10 py-4 rounded-md hover:bg-gray-100 transition font-medium text-lg inline-block" data-testid="services-get-quote-btn">
              Request a Free Quote
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;